/* UI130: scoped navigation motion. No purchase handlers or navigation delays. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();
  const active = new Set();
  const easing = 'cubic-bezier(.22,1,.36,1)';

  class Disclosure {
    constructor(details, panel, kind = 'accordion', hooks = {}) {
      this.details = details;
      this.summary = details.querySelector(':scope > summary');
      this.panel = panel;
      this.kind = kind;
      this.hooks = hooks;
      this.expanded = details.open;
      this.nativeOpen = details.open;
      details.classList.add('bw-menu-disclosure');
      panel.classList.add('bw-menu-panel');
      this.summary.setAttribute('aria-expanded', String(this.expanded));
      if (panel.id) this.summary.setAttribute('aria-controls', panel.id);
      panel.inert = !this.expanded;
      details.addEventListener('toggle', () => {
        // Respect external/native close (including BFCache and theme editor).
        if (details.open !== this.nativeOpen) this.set(details.open, true);
      });
      controllers.set(details, this);
    }

    set(expanded, immediate = false) {
      const { details, panel, summary } = this;
      if (this.expanded === expanded && !this.animation && details.open === expanded) return;
      freezeFooterBackground(details);
      const wasVisible = details.open;
      const height = wasVisible ? panel.getBoundingClientRect().height : 0;
      const current = getComputedStyle(panel);
      const currentOpacity = wasVisible ? current.opacity : '0';
      const currentTransform = wasVisible ? current.transform : 'translateX(-24px)';
      const paddingTop = wasVisible ? current.paddingTop : '0px';
      const paddingBottom = wasVisible ? current.paddingBottom : '0px';
      this.cancel();
      this.expanded = expanded;
      summary.setAttribute('aria-expanded', String(expanded));
      if (!expanded && panel.contains(document.activeElement)) summary.focus({ preventScroll: true });
      panel.inert = !expanded;
      if (expanded) details.open = true;
      this.nativeOpen = details.open;
      this.hooks.start?.(expanded);
      const finish = () => {
        this.cancel();
        details.open = expanded;
        this.nativeOpen = expanded;
        this.hooks.finish?.(expanded);
      };
      this.finish = finish;
      if (immediate || reduce.matches || !panel.animate || !details.isConnected || !wasVisible && !expanded) {
        finish();
        return;
      }
      const natural = getComputedStyle(panel);
      const frames = this.kind === 'drawer'
        ? [{ opacity: currentOpacity, transform: currentTransform }, { opacity: expanded ? 1 : 0, transform: expanded ? 'translateX(0)' : 'translateX(-24px)' }]
        : [{ height: `${height}px`, paddingTop, paddingBottom, opacity: currentOpacity }, { height: expanded ? `${panel.scrollHeight}px` : '0px', paddingTop: expanded ? natural.paddingTop : '0px', paddingBottom: expanded ? natural.paddingBottom : '0px', opacity: expanded ? 1 : 0 }];
      this.animation = panel.animate(frames, { duration: expanded ? 280 : 220, easing });
      active.add(this);
      this.animation.onfinish = finish;
    }

    cancel() {
      if (this.animation) {
        this.animation.onfinish = null;
        this.animation.cancel();
        this.animation = null;
      }
      active.delete(this);
    }
  }

  // Footer cover used to rescale as an accordion changed the footer height.
  // Resolve the original cover size once per width, independent of panel height.
  const footerWidths = new WeakMap();
  const footerImages = new WeakMap();
  function freezeFooterBackground(details) {
    if (!details.matches('[data-bw-footer-menu]')) return;
    const footer = details.closest('footer');
    if (!footer || getComputedStyle(footer).backgroundImage === 'none') return;
    const rect = footer.getBoundingClientRect();
    if (!rect.width || footerWidths.get(footer) === rect.width) return;
    let closedHeight = rect.height;
    footer.querySelectorAll('details[data-bw-footer-menu][open] > .footer-column-options').forEach(panel => {
      closedHeight -= panel.getBoundingClientRect().height;
    });
    // Existing footer-bg.png is 3840 x 1346 in both regional themes.
    const scale = Math.max(rect.width / 3840, closedHeight / 1346);
    if (!footerImages.has(footer)) footerImages.set(footer, getComputedStyle(footer).backgroundImage);
    footer.style.setProperty('background-image', `linear-gradient(to bottom, transparent 75%, #111 100%), ${footerImages.get(footer)}`, 'important');
    footer.style.setProperty('background-size', `${3840 * scale}px ${1346 * scale}px`, 'important');
    footer.style.setProperty('background-position', 'center top', 'important');
    footer.style.setProperty('background-color', '#111', 'important');
    footerWidths.set(footer, rect.width);
  }
  window.addEventListener('resize', () => {
    document.querySelectorAll('details[data-bw-footer-menu]').forEach(freezeFooterBackground);
  }, { passive: true });

  function accordion(details) {
    if (controllers.has(details)) return controllers.get(details);
    const panel = details.querySelector(':scope > .menu-drawer__submenu, :scope > .footer-column-options');
    if (!panel) return null;
    return new Disclosure(details, panel);
  }

  window.BWMenuMotion = {
    header(drawer) {
      const details = drawer.mainDetailsToggle;
      const summary = details.querySelector(':scope > summary');
      const header = drawer.closest('.section-header');
      const panel = details.querySelector(':scope > .menu-drawer');
      if (!panel || !header) return null;
      drawer.classList.add('bw-drawer-motion');
      const updatePosition = () => {
        const border = drawer.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
        document.documentElement.style.setProperty('--header-bottom-position', `${Math.floor(header.getBoundingClientRect().bottom - border)}px`);
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      };
      const nested = Array.from(panel.querySelectorAll('details')).map(accordion).filter(Boolean);
      const root = new Disclosure(details, panel, 'drawer', {
        start(open) {
          if (open) {
            updatePosition();
            details.classList.add('menu-opening');
            header.classList.add('menu-open');
            document.body.classList.add(`overflow-hidden-${drawer.dataset.breakpoint}`);
            trapFocus(details, summary);
          } else {
            removeTrapFocus(summary);
          }
        },
        finish(open) {
          if (open) return;
          details.classList.remove('menu-opening');
          header.classList.remove('menu-open');
          document.body.classList.remove(`overflow-hidden-${drawer.dataset.breakpoint}`);
          nested.forEach(item => item.set(false, true));
        }
      });
      const resize = () => {
        if (!drawer.isConnected) { window.removeEventListener('resize', resize); return; }
        nested.forEach(item => { if (item.animation) item.finish(); });
        if (root.expanded && !drawer.getClientRects().length) root.set(false, true);
        else if (root.expanded) updatePosition();
      };
      window.addEventListener('resize', resize, { passive: true });
      return {
        click(event) {
          const item = controllers.get(event.currentTarget.parentElement);
          if (!item) return;
          event.preventDefault();
          item.set(!item.expanded);
        },
        key(event) {
          if ((event.key || event.code).toUpperCase() !== 'ESCAPE') return;
          const item = controllers.get(event.target.closest('details[open]')) || root;
          event.preventDefault();
          item.set(false);
        },
        open() { root.set(true); },
        close() { root.set(false); },
        closeSubmenu(details) { controllers.get(details)?.set(false); }
      };
    }
  };

  function initFooter(scope = document) {
    scope.querySelectorAll('details[data-bw-footer-menu]').forEach(details => {
      if (controllers.has(details)) return;
      const item = accordion(details);
      if (!item) return;
      item.summary.addEventListener('click', event => { event.preventDefault(); item.set(!item.expanded); });
      details.addEventListener('keydown', event => {
        if (event.key === 'Escape' && item.expanded) { event.preventDefault(); item.set(false); }
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initFooter(), { once: true });
  else initFooter();
  document.addEventListener('shopify:section:load', event => initFooter(event.target));
  reduce.addEventListener('change', () => { if (reduce.matches) Array.from(active).forEach(item => item.finish()); });
  window.addEventListener('pagehide', () => Array.from(active).forEach(item => item.finish()));
})();
